/**
 * THE TWO SCREENS A COLLECTION NEEDS BESIDES ITS DATA AND ITS EMPTY STATE.
 *
 * ============================================================================
 * WHY THESE ARE SHARED AND NOT PER PAGE. stores/loadState.ts gave the app four read
 * states — idle, loading, ready, error — and the audit found that every collection screen
 * still branched on `items.length === 0` alone, which collapses three of them into one.
 * The consequence is not cosmetic: a request that 500s renders "You have no songs", so an
 * outage is reported to the user as data loss.
 *
 * Fixing that needs a skeleton and a failure state on six screens. Writing them per screen
 * would be six loading systems — the thing the brief rules out — and six chances for one
 * of them to keep using `length === 0`. So they live here, take `LoadState` derivatives as
 * props, and hold no store access of their own (the rule for this directory).
 *
 * WHAT IS DELIBERATELY NOT HERE: the empty state. EmptyState already exists and every
 * screen's designed empty copy is its own — "No tracks yet" is not "No playlists yet", and
 * collapsing them would lose writing that is doing real work. These two are the states that
 * had NO component at all.
 * ============================================================================
 *
 * ============================================================================
 * DASHBOARD'S `LoadingTiles` IS NOT REPLACED BY THIS, and that is deliberate. It renders
 * the Explore grid's own tile geometry, so it is a skeleton OF something specific rather
 * than a generic one; swapping it for these rows would make the first paint of the home
 * page a worse approximation of what follows it. This is for the list-shaped screens,
 * which is what the other five are.
 * ============================================================================
 */
import type { CSSProperties, ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';
import Button from './Button';
import { c } from '../../styles/theme';

/**
 * Off-screen but announced. Same declarations as forms/RadioCard.tsx's — the app's
 * existing convention for this, kept as an inline style rather than a new global class so
 * there is one way to do it rather than two.
 */
const VISUALLY_HIDDEN: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

export interface LoadSkeletonProps {
  /** How many placeholder rows to draw. Defaults to 3 — enough to read as a list. */
  rows?: number;
  /** Row height in px. Defaults to a list row; a grid of cards passes something taller. */
  rowHeight?: number;
  /** Announced while the rows are on screen. Name the thing, e.g. "Loading your songs…". */
  label: string;
}

/**
 * Placeholder rows shown while a FIRST load is in flight.
 *
 * SHOWN ONLY WHEN THERE IS NOTHING BEHIND IT — the caller decides that with
 * `isFirstLoad(load, hasData)`, which is exactly why that predicate takes `hasData`. A
 * refresh over a populated list must not blank it: re-reading the library you are looking
 * at should not make it disappear and come back.
 *
 * THE LABEL IS NOT DECORATIVE. `role="status"` with a real sentence, because a screen
 * reader on a set of empty grey boxes announces nothing, and the page appears to have
 * stopped. The boxes themselves are `aria-hidden`: they convey the shape of what is coming
 * to a sighted user and would be noise read aloud.
 *
 * NO SHIMMER ANIMATION. A pulsing gradient is the thing `prefers-reduced-motion` exists to
 * suppress, and it would need a motion exception for no gain — the sentence already says
 * what is happening. The rows are static and low-contrast.
 */
export function LoadSkeleton({ rows = 3, rowHeight = 64, label }: LoadSkeletonProps) {
  return (
    <div>
      <p role="status" style={VISUALLY_HIDDEN}>
        {label}
      </p>
      <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {Array.from({ length: rows }, (_, i) => (
          <div
            key={i}
            style={{
              height: rowHeight,
              borderRadius: 10,
              background: c.surface2,
              border: `1px solid ${c.border}`,
              // Each row slightly fainter than the last, so the block reads as "more
              // below" rather than as a solid slab of placeholder.
              opacity: 1 - i * 0.18,
            }}
          />
        ))}
      </div>
    </div>
  );
}

export interface LoadErrorProps {
  /**
   * The message from `LoadState.error.message`.
   *
   * Already a human sentence: api/errors.ts guarantees one for every failure kind, and
   * the §2 contract says a server's `message` is written to be shown verbatim. Screens
   * pass it through rather than writing their own, so the app has one voice for failure.
   */
  message: string;
  /** Re-runs the store's load action. Omitted where a screen has no retry to offer. */
  onRetry?: () => void;
  /** Extra context, e.g. what will still work while this is broken. */
  children?: ReactNode;
}

/**
 * Shown when a read FAILED — the state that previously had no representation anywhere.
 *
 * ============================================================================
 * THIS IS THE COMPONENT THAT STOPS AN OUTAGE READING AS AN EMPTY ACCOUNT. It exists so a
 * screen can answer "the request failed" with something other than its empty state, and
 * its visual language is deliberately different: a warning icon and a solid border, where
 * EmptyState is a dashed box with an invitation. "You have no songs, create one" and
 * "we could not load your songs" must not look alike.
 *
 * AND IT DOES NOT INVENT DATA TO MAKE ITSELF GO AWAY. There is no fallback fixture behind
 * this and no "showing cached results" claim — the local data, if any, is left alone by
 * the store (see loadState.ts: "the local data is untouched") and the screen decides
 * whether it has anything worth rendering beside this notice.
 * ============================================================================
 *
 * `role="alert"` because it replaces content the user asked for; its arrival is not
 * something a screen-reader user should have to go looking for.
 */
export function LoadError({ message, onRetry, children }: LoadErrorProps) {
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        // Wraps: the icon, the sentence and the button do not fit on one line at 380px,
        // and a fixed row would push the button past the container's edge.
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: 12,
        padding: '18px 16px',
        borderRadius: 12,
        border: `1px solid ${c.border}`,
        background: c.surface1,
      }}
    >
      <AlertCircle size={18} color={c.danger} style={{ flexShrink: 0, marginTop: 1 }} />
      {/* `min-width: 0` releases the flex item's automatic minimum size, so a long
          message wraps inside the box instead of widening it — the same rule the Studio
          panels state. `flex: 1` on a 240px basis keeps the button beside the text on a
          desktop and drops it below on a phone. */}
      <div style={{ flex: '1 1 240px', minWidth: 0 }}>
        <p
          style={{
            fontSize: 13,
            fontWeight: 500,
            margin: 0,
            color: c.text1,
            fontFamily: 'Space Grotesk',
          }}
        >
          Could not load this
        </p>
        <p
          style={{
            fontSize: 12,
            color: c.text2,
            margin: '3px 0 0',
            lineHeight: 1.55,
            overflowWrap: 'anywhere',
          }}
        >
          {message}
        </p>
        {children}
      </div>
      {onRetry && (
        <Button variant="secondary" onClick={onRetry} style={{ flexShrink: 0 }}>
          Try again
        </Button>
      )}
    </div>
  );
}
